import { commonValues } from '@/config/common.js';
import { useCurrencyStore } from '@/stores/currency';

export function useApiSeo() {
  const { $api } = useNuxtApp();
  const { locale } = useI18n();
  const currencyStore = useCurrencyStore();
  const languageStore = useLanguageStore();

  const userCurrency = currencyStore.getCurrentCurrency;
  const userLanguage = languageStore.getCurrentLanguage;

  const defaultQuery = { currency: userCurrency?.code };

  const defaultHeaders = { 'Accept-Language': userLanguage?.tag };

  const defaultParams = {};

  const defaultResponse = {
    copy: '',
    description: '',
    title: '',
    keywords: '',
    heading: null,
    ogTitle: '',
    ogDescription: '',
    ogImage: commonValues.OGIMAGE,
    ogUrl: '',
    h1: '',
    h2: '',
  };

  const getLegalPageTags = async (page) => {
    if (!page) {
      return null;
    }

    const callKey = `getLegalPageTags${locale?.value}${page}${userCurrency?.code}`;
    const endpoint = `seo/generic/${page}/`;

    const result = await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams);

    return result?.data || null;
  };

  const getGenericPageTags = async (page) => {
    const callKey = `getGenericPageTags${locale?.value}${page}${userCurrency?.code}`;
    const endpoint = `seo/generic/${page}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getHomePageTags = async () => {
    const callKey = `getHomePageTags${locale?.value}${userCurrency?.code}`;
    const endpoint = 'seo/homepage/';

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getWorldPageTags = async (propertyType) => {
    const internalQuery = {
      propertyType,
      ...defaultQuery,
    };

    const callKey = `getWorldPageTags${locale?.value}${propertyType}${userCurrency?.code}`;
    const endpoint = 'seo/worldwide/';

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, internalQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getContinentPageTags = async (propertyType, continentId) => {
    const internalQuery = {
      propertyType,
      ...defaultQuery,
    };

    const callKey = `getContinentPageTags${locale?.value}${propertyType}${continentId}${userCurrency?.code}`;
    const endpoint = `seo/continent/${continentId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, internalQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getCountryPageTags = async (propertyType, countryId) => {
    const internalQuery = {
      propertyType,
      ...defaultQuery,
    };

    const callKey = `getCountryPageTags${locale?.value}${propertyType}${countryId}${userCurrency?.code}`;
    const endpoint = `seo/country/${countryId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, internalQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getAreaPageTags = async (propertyType, areaId) => {
    const internalQuery = {
      propertyType,
      ...defaultQuery,
    };

    const callKey = `getAreaPageTags${locale?.value}${propertyType}${areaId}${userCurrency?.code}`;
    const endpoint = `seo/area/${areaId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, internalQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getRegionPageTags = async (propertyType, regionId) => {
    const internalQuery = {
      propertyType,
      ...defaultQuery,
    };

    const callKey = `getRegionPageTags${locale?.value}${propertyType}${regionId}${userCurrency?.code}`;
    const endpoint = `seo/region/${regionId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, internalQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getCityPageTags = async (propertyType, cityId) => {
    const internalQuery = {
      propertyType,
      ...defaultQuery,
    };

    const callKey = `getCityPageTags${locale?.value}${propertyType}${cityId}${userCurrency?.code}`;
    const endpoint = `seo/city/${cityId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, internalQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getDistrictPageTags = async (propertyType, districtId) => {
    const internalQuery = {
      propertyType,
      ...defaultQuery,
    };

    const callKey = `getDistrictPageTags${locale?.value}${propertyType}${districtId}${userCurrency?.code}`;
    const endpoint = `seo/district/${districtId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, internalQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };
  const getPropertyPageTags = async (propertyType, propertyId) => {
    const internalQuery = {
      propertyType,
      ...defaultQuery,
    };

    const callKey = `getPropertyPageTags${locale?.value}${propertyType}${propertyId}${userCurrency?.code}`;
    const endpoint = `seo/microsite/${propertyId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, internalQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getHostelChainTags = async () => {
    const callKey = `getHostelChainTags${locale?.value}${userCurrency?.code}`;
    const endpoint = '/seo/hostel-group/';

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getEventsWorldPageTags = async () => {
    const callKey = `getEventsWorldPageTags${locale?.value}${userCurrency?.code}`;
    const endpoint = 'seo/events/worldwide/';

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getEventsCityPageTags = async (cityId) => {
    const callKey = `getCityEventsPageTags${locale?.value}${cityId}`;
    const endpoint = `seo/events/city/${cityId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getEventsCountryPageTags = async (countryId) => {
    const callKey = `getCountryEventsPageTags${locale?.value}${countryId}`;
    const endpoint = `seo/events/country/${countryId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  const getEventsContinentPageTags = async (continentId) => {
    if (!continentId) {
      return defaultResponse;
    }

    const callKey = `getContinentEventsPageTags${locale?.value}${continentId}`;
    const endpoint = `seo/events/continent/${continentId}/`;

    const result = ref(await $api.spapi(callKey, endpoint, defaultHeaders, defaultQuery, defaultParams));

    return result?.value?.data || defaultResponse;
  };

  return {
    getGenericPageTags,
    getLegalPageTags,
    getHomePageTags,
    getWorldPageTags,
    getContinentPageTags,
    getCountryPageTags,
    getAreaPageTags,
    getRegionPageTags,
    getCityPageTags,
    getDistrictPageTags,
    getPropertyPageTags,
    getHostelChainTags,
    getEventsWorldPageTags,
    getEventsCityPageTags,
    getEventsContinentPageTags,
    getEventsCountryPageTags,
  };
}
